import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { useState } from 'react'
import { CricketHeader } from '#/components/layout/CricketHeader'
import { AutocompleteSelect } from '#/components/ui/AutocompleteSelect'
import { useCricketStore } from '#/store/cricketStore'

export const Route = createFileRoute('/match/openers')({
  validateSearch: (search: Record<string, unknown>): { inning?: number } => {
    return {
      inning: Number(search.inning) || 1,
    }
  },
  component: SelectOpenersScreen,
})

function SelectOpenersScreen() {
  const navigate = useNavigate()
  const search = useSearch({ from: '/match/openers' })
  const match = useCricketStore((state) => state.currentMatch)
  const setOpeningPlayers = useCricketStore((state) => state.setOpeningPlayers)
  const startSecondInnings = useCricketStore((state) => state.startSecondInnings)
  const teamsList = useCricketStore((state) => state.teamsList)
  const savedPlayerNames = useCricketStore((state) => state.savedPlayerNames)

  const isInning2 = search.inning === 2 || match?.currentInnings === 2

  const [striker, setStriker] = useState('')
  const [nonStriker, setNonStriker] = useState('')
  const [openingBowler, setOpeningBowler] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  if (!match) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-600 mb-4">No match initialized.</p>
        <button
          onClick={() => navigate({ to: '/' })}
          className="bg-[#2E7D32] text-white px-4 py-2 rounded-md"
        >
          Create Match
        </button>
      </div>
    )
  }

  // Determine current active batting and bowling teams for this inning
  // If we are selecting openers for inning 2 (and match.currentInnings is still 1), swap the teams
  const effectiveBattingTeam = isInning2 && match.currentInnings === 1
    ? match.bowlingTeam
    : match.battingTeam
  const effectiveBowlingTeam = isInning2 && match.currentInnings === 1
    ? match.battingTeam
    : match.bowlingTeam

  const isSinglePlayer = Boolean(match.settings.singlePlayerMode || match.settings.playersPerTeam === 1)

  // Filter player suggestions by the active batting team and bowling team
  const battingTeamRecord = teamsList.find(
    (t) => t.name.toLowerCase() === effectiveBattingTeam.toLowerCase()
  )
  const bowlingTeamRecord = teamsList.find(
    (t) => t.name.toLowerCase() === effectiveBowlingTeam.toLowerCase()
  )

  // Batting team players (or fallback to saved names if team has no players registered yet)
  const battingTeamPlayers = battingTeamRecord?.players?.length
    ? battingTeamRecord.players
    : savedPlayerNames

  // Bowling team players: in 2nd innings prioritize batsmen who played in 1st innings
  const firstInningsBatsmen = isInning2
    ? Object.keys(match.innings1?.batsmen || {})
    : []

  const bowlingTeamPlayers = bowlingTeamRecord?.players?.length
    ? bowlingTeamRecord.players
    : (firstInningsBatsmen.length > 0 ? firstInningsBatsmen : savedPlayerNames)

  const suggestedBowlersList =
    isInning2 && firstInningsBatsmen.length > 0
      ? Array.from(new Set([...firstInningsBatsmen, ...bowlingTeamPlayers]))
      : bowlingTeamPlayers

  const handleStartMatch = (e: React.FormEvent) => {
    e.preventDefault()
    if (!striker.trim() || !openingBowler.trim()) {
      setErrorMessage('Please enter both Batsman and Bowler names')
      return
    }

    if (!isSinglePlayer) {
      if (!nonStriker.trim()) {
        setErrorMessage('Please enter non-striker player name')
        return
      }
      if (striker.trim().toLowerCase() === nonStriker.trim().toLowerCase()) {
        setErrorMessage('Striker and Non-striker must be different players')
        return
      }
    }

    setErrorMessage('')
    const effectiveNonStriker = isSinglePlayer ? '' : nonStriker.trim()
    if (isInning2) {
      startSecondInnings(striker.trim(), effectiveNonStriker, openingBowler.trim())
    } else {
      setOpeningPlayers(striker.trim(), effectiveNonStriker, openingBowler.trim())
    }

    navigate({ to: '/match/$matchId', params: { matchId: match.id } })
  }

  return (
    <div className="flex-1 flex flex-col justify-between min-h-screen bg-[#f5f6f8]">
      <div>
        <CricketHeader
          title="Select Opening players"
          showActions={false}
          showBack={true}
          onBack={() => navigate({ to: '/' })}
        />

        {/* Datalist for player auto-suggestions */}
        <datalist id="suggested-players">
          {savedPlayerNames.map((p) => (
            <option key={p} value={p} />
          ))}
        </datalist>

        <datalist id="suggested-bowlers">
          {suggestedBowlersList.map((p) => (
            <option key={p} value={p} />
          ))}
        </datalist>

        <form onSubmit={handleStartMatch} className="p-4 space-y-6">
          {errorMessage && (
            <div className="bg-red-50 text-red-700 text-xs font-semibold px-3 py-2 rounded-lg border border-red-200">
              {errorMessage}
            </div>
          )}

          {/* Striker */}
          <div>
            <label className="text-sm font-semibold theme-text-primary block mb-2">
              {isSinglePlayer ? 'Batsman' : 'Striker'} ({effectiveBattingTeam})
            </label>
            <AutocompleteSelect
              placeholder="Player name"
              value={striker}
              onChange={setStriker}
              options={battingTeamPlayers.filter((p) => p.toLowerCase() !== nonStriker.toLowerCase())}
              required
            />
          </div>

          {/* Non-striker - only shown for multi-player teams */}
          {!isSinglePlayer && (
            <div>
              <label className="text-sm font-semibold theme-text-primary block mb-2">
                Non-striker ({effectiveBattingTeam})
              </label>
              <AutocompleteSelect
                placeholder="Player name"
                value={nonStriker}
                onChange={setNonStriker}
                options={battingTeamPlayers.filter((p) => p.toLowerCase() !== striker.toLowerCase())}
                required
              />
            </div>
          )}

          {/* Opening bowler */}
          <div>
            <label className="text-sm font-semibold theme-text-primary block mb-2">
              Opening bowler ({effectiveBowlingTeam})
            </label>
            <AutocompleteSelect
              placeholder="Player name"
              value={openingBowler}
              onChange={setOpeningBowler}
              options={suggestedBowlersList}
              required
            />
          </div>

          <div className="pt-6">
            <button
              type="submit"
              className="w-full theme-btn-primary font-semibold py-3.5 rounded-lg shadow-sm active:scale-98 transition-all text-base cursor-pointer"
            >
              Start match
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
